import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Button from '../UI/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPaperclip, FiX, FiFile, FiUploadCloud, FiRepeat, FiCalendar } = FiIcons;

const TaskForm = ({ task, onSubmit, onCancel, availableUsers, existingProjects = [] }) => {
  const { users: contextUsers } = useCRM();
  const { user: currentUser, isAdmin, isManager } = useAuth();

  // Use availableUsers prop if provided, otherwise fall back to context users
  const users = availableUsers && availableUsers.length > 0 ? availableUsers : contextUsers;

  // Initialize existing attachments state
  // Handle migration: check task.attachments first, fallback to legacy attachment_url
  const [existingAttachments, setExistingAttachments] = useState(() => {
    if (task?.attachments && Array.isArray(task.attachments)) {
      return task.attachments;
    }
    if (task?.attachment_url) {
      return [{ url: task.attachment_url, name: 'Attachment', type: 'legacy' }];
    }
    return [];
  });

  const [newFiles, setNewFiles] = useState([]);
  const MAX_FILES = 4;

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: task || {
      title: '',
      description: '',
      assignedTo: (!isAdmin && !isManager) ? currentUser.id : '',
      priority: 'medium',
      status: 'todo',
      startDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      estimatedHours: '',
      projectId: '',
      tags: '',
      notes: '',
      recurrence_type: 'none',
      recurrence_interval: 1
    }
  });

  const recurrenceType = watch('recurrence_type');

  const priorities = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  const statuses = [
    { value: 'todo', label: 'To Do' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'review', label: 'Under Review' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const recurrenceOptions = [
    { value: 'none', label: 'No Recurrence' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalCurrentFiles = existingAttachments.length + newFiles.length;
    
    if (totalCurrentFiles + files.length > MAX_FILES) {
      alert(`You can only upload a maximum of ${MAX_FILES} files.`);
      return;
    }
    
    setNewFiles(prev => [...prev, ...files]);
    // Reset input so same file can be selected again if needed
    e.target.value = '';
  };

  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (index) => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = (data) => {
    // Validate required fields
    if (!data.title?.trim()) {
      console.error('Title is required');
      return;
    }
    if (!data.description?.trim()) {
      console.error('Description is required');
      return;
    }
    if (!data.assignedTo && (isAdmin || isManager)) {
      console.error('Assigned user is required');
      return;
    }

    // Combine form data with attachment state
    const submitData = {
      ...data,
      existingAttachments, // Pass remaining existing attachments
      newFiles, // Pass new files to upload
      // Ensure recurrence interval is a number if recurrence is active
      recurrence_interval: data.recurrence_type !== 'none' ? parseInt(data.recurrence_interval) : null
    };
    onSubmit(submitData);
  };

  const canAssignOthers = isAdmin || isManager;
  const currentTotalFiles = existingAttachments.length + newFiles.length;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task Title *
          </label>
          <input
            type="text"
            {...register('title', { required: 'Task title is required', minLength: { value: 3, message: 'Title must be at least 3 characters' } })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter task title"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assign To *
          </label>
          {canAssignOthers ? (
            <select
              {...register('assignedTo', { required: 'Please assign to someone' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select employee</option>
              {users && users.length > 0 ? (
                users
                  .filter(user => user.is_active !== false && user.isActive !== false)
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.fullName} ({user.role})
                    </option>
                  ))
              ) : (
                <option value="" disabled>No active users available</option>
              )}
            </select>
          ) : (
            <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600">
              {currentUser.full_name} (Self)
              <input type="hidden" {...register('assignedTo')} value={currentUser.id} />
            </div>
          )}
          {errors.assignedTo && (
            <p className="mt-1 text-sm text-red-600">{errors.assignedTo.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <select
            {...register('priority')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            {priorities.map(priority => (
              <option key={priority.value} value={priority.value}>
                {priority.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            {...register('status')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date *
          </label>
          <input
            type="date"
            {...register('startDate', { required: 'Start date is required' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
          {errors.startDate && (
            <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Due Date
          </label>
          <input
            type="date"
            {...register('dueDate')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Recurrence Section */}
        <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <div className="flex items-center mb-3">
            <SafeIcon icon={FiRepeat} className="h-4 w-4 text-blue-600 mr-2" />
            <h4 className="text-sm font-semibold text-blue-900">Recurring Schedule</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
              <select
                {...register('recurrence_type')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                {recurrenceOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {recurrenceType !== 'none' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Interval (Every X {recurrenceType === 'daily' ? 'days' : recurrenceType === 'weekly' ? 'weeks' : recurrenceType === 'monthly' ? 'months' : 'years'})
                </label>
                <input
                  type="number"
                  min="1"
                  {...register('recurrence_interval')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            )}
          </div>
          {recurrenceType !== 'none' && (
            <p className="text-xs text-blue-600 mt-2 flex items-center">
              <SafeIcon icon={FiCalendar} className="h-3 w-3 mr-1" />
              Next task will be created automatically when this one is completed.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estimated Hours
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            {...register('estimatedHours')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter estimated hours"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Project/Reference
          </label>
          <input
            type="text"
            list="project-suggestions"
            {...register('projectId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Select or type project name"
            autoComplete="off"
          />
          <datalist id="project-suggestions">
            {existingProjects.map((proj, index) => (
              <option key={index} value={proj} />
            ))}
          </datalist>
          <p style={{
      color: 'rgb(107, 114, 128)'
    }} className="text-xs text-gray-500 mt-1">Example: aatma brochure design. Specifically use the same name for all the tasks which will come under this grouping.</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <input
            type="text"
            {...register('tags')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter tags separated by commas (e.g., urgent, frontend, bug)"
          />
        </div>

        {/* Attachments Section */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachments ({currentTotalFiles}/{MAX_FILES})
          </label>
          <div className="border border-gray-300 border-dashed rounded-lg p-4 bg-gray-50">
            {/* Existing Attachments List */}
            {existingAttachments.length > 0 && (
              <div className="space-y-2 mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase">Current Files</p>
                {existingAttachments.map((file, index) => (
                  <div key={`existing-${index}`} className="flex items-center justify-between bg-white p-2 border border-gray-200 rounded shadow-sm">
                    <div className="flex items-center space-x-2 truncate">
                      <SafeIcon icon={FiPaperclip} className="h-4 w-4 text-primary-500" />
                      <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-xs">
                        {file.name || 'Attachment'}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExistingAttachment(index)}
                      className="text-gray-400 hover:text-red-500 p-1"
                      title="Remove attachment"
                    >
                      <SafeIcon icon={FiX} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* New Files List */}
            {newFiles.length > 0 && (
              <div className="space-y-2 mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase">New Uploads</p>
                {newFiles.map((file, index) => (
                  <div key={`new-${index}`} className="flex items-center justify-between bg-blue-50 p-2 border border-blue-100 rounded shadow-sm">
                    <div className="flex items-center space-x-2 truncate">
                      <SafeIcon icon={FiFile} className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700 truncate max-w-xs">{file.name}</span>
                      <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNewFile(index)}
                      className="text-gray-400 hover:text-red-500 p-1"
                      title="Remove file"
                    >
                      <SafeIcon icon={FiX} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            {currentTotalFiles < MAX_FILES ? (
              <div className="mt-2">
                <label className="cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 w-full sm:w-auto">
                  <SafeIcon icon={FiUploadCloud} className="h-5 w-5 mr-2 text-gray-400" />
                  <span>Choose Files</span>
                  <input type="file" multiple className="sr-only" onChange={handleFileChange} />
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Max 4 files. Supported formats: Images, PDF, Docs.
                </p>
              </div>
            ) : (
              <p className="text-sm text-orange-600 text-center py-2 bg-orange-50 rounded">
                Maximum file limit reached. Remove files to add new ones.
              </p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description *
        </label>
        <textarea
          {...register('description', { required: 'Description is required', minLength: { value: 10, message: 'Description must be at least 10 characters' } })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          placeholder="Describe the task in detail"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Additional Notes
        </label>
        <textarea
          {...register('notes')}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          placeholder="Any additional notes or instructions"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {task ? 'Update Task' : 'Create Task'}
        </Button>
      </div>
    </form>
  );
};

export default TaskForm;